/**
 * Quick Summary Card - Phase 2 Component
 * Provides at-a-glance weekly status with key metrics
 * Location: Hero Section (3rd card)
 */

import { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { 
  Zap,
  Clock,
  CheckCircle,
  AlertCircle,
  TrendingUp,
  ArrowRight,
  ExternalLink
} from 'lucide-react';
import { useTimesheetForScreen } from '@/contexts/TimesheetContext';
import { format, startOfWeek, addDays } from 'date-fns';
import { useOrganizationSettings } from '@/hooks/useOrganizationSettings';

interface QuickSummaryCardProps {
  onNavigate?: (path: string) => void;
}

export function QuickSummaryCard({ onNavigate }: QuickSummaryCardProps) {
  const { timesheets, currentEmployee, projects } = useTimesheetForScreen('dashboard');
  const { settings } = useOrganizationSettings('dashboard');

  // Don't render until settings are loaded from the API
  // This ensures we never show default values (like 40h) before the actual API data
  if (!settings) {
    return null;
  }

  const summaryData = useMemo(() => {
    const currentWeek = startOfWeek(new Date(), { weekStartsOn: 1 });
    const currentWeekEnd = addDays(currentWeek, 6);
    const today = new Date();

    // Find current week timesheet
    const currentTimesheet = timesheets.find(
      ts => ts.employeeId === currentEmployee.id &&
      format(ts.weekEnding, 'yyyy-MM-dd') === format(currentWeekEnd, 'yyyy-MM-dd')
    );

    // Calculate hours logged
    const hoursLogged = currentTimesheet?.entries.reduce((sum, entry) => {
      return sum + Object.values(entry.hours).reduce((h, v) => h + v, 0);
    }, 0) || 0;

    // Get expected hours: prefer organization settings (which are the source of truth),
    // only use employee's custom working pattern if they have one that differs from org default
    // Note: currentEmployee.workingPattern from mock data should not override org settings
    const expectedHours = settings.workingProfile?.standardHoursPerWeek 
      || currentEmployee?.workingPattern?.hoursPerWeek 
      || 40;
    const progressPercentage = Math.min((hoursLogged / expectedHours) * 100, 100);

    // Calculate billable hours
    const billableHours = currentTimesheet?.entries.reduce((sum, entry) => {
      const project = projects.find(p => p.id === entry.projectId);
      const hours = Object.values(entry.hours).reduce((h, v) => h + v, 0);
      return sum + (project?.billable ? hours : 0);
    }, 0) || 0;

    const billablePercentage = hoursLogged > 0 ? (billableHours / hoursLogged) * 100 : 0;

    // Count entries
    const entriesCount = currentTimesheet?.entries.length || 0;

    // Calculate days with entries
    const daysWithEntries = currentTimesheet?.entries.reduce((days, entry) => {
      Object.entries(entry.hours).forEach(([day, hours]) => {
        if (hours > 0) days.add(day);
      });
      return days;
    }, new Set<string>()).size || 0;

    // Determine status
    const status = currentTimesheet?.status || 'draft';

    // Calculate due date using configurable deadline from organization settings (live or demo mode)
    // deadlineDay is a day-of-week value (0=Sunday, 1=Monday, ..., 5=Friday, 6=Saturday)
    // We need to calculate the offset from the week start to the deadline day
    const deadline = new Date(currentWeek);
    const baseDayOfWeek = deadline.getDay(); // 0=Sunday, 1=Monday, etc.
    const targetDayOfWeek = settings.deadline.deadlineDay;
    const offset = (targetDayOfWeek - baseDayOfWeek + 7) % 7;
    deadline.setDate(deadline.getDate() + offset);
    const [hours, minutes] = settings.deadline.deadlineTime.split(':').map(Number);
    deadline.setHours(hours, minutes, 0, 0);
    
    // Calculate days until deadline
    const diffMs = deadline.getTime() - today.getTime();
    const daysUntilDue = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    // Determine overall status
    let overallStatus: 'on-track' | 'needs-attention' | 'critical' | 'complete';
    let statusMessage: string;

    if (status === 'approved' || status === 'locked') {
      overallStatus = 'complete';
      statusMessage = 'Week Complete';
    } else if (status === 'submitted') {
      overallStatus = 'complete';
      statusMessage = 'Awaiting Approval';
    } else if (daysUntilDue < 0) {
      overallStatus = 'critical';
      statusMessage = 'Overdue';
    } else if (daysUntilDue === 0 && hoursLogged < expectedHours * 0.8) {
      overallStatus = 'critical';
      statusMessage = 'Due Today';
    } else if (daysUntilDue <= 1 && hoursLogged < expectedHours * 0.5) {
      overallStatus = 'needs-attention';
      statusMessage = 'Needs Attention';
    } else if (hoursLogged >= expectedHours * 0.75) {
      overallStatus = 'on-track';
      statusMessage = 'On Track';
    } else {
      overallStatus = 'on-track';
      statusMessage = 'In Progress';
    }

    return {
      hoursLogged,
      expectedHours,
      progressPercentage,
      billableHours,
      billablePercentage,
      entriesCount,
      daysWithEntries,
      status,
      overallStatus,
      statusMessage,
      daysUntilDue,
      deadlineTime: settings.deadline.deadlineTime,
      hasTimesheet: !!currentTimesheet
    };
  }, [timesheets, currentEmployee, projects, settings]);

  // Status configuration
  const statusConfig = {
    'on-track': {
      accentClass: 'accent-success',
      icon: CheckCircle,
      variant: 'default' as const
    },
    'needs-attention': {
      accentClass: 'accent-warning',
      icon: AlertCircle,
      variant: 'secondary' as const
    },
    'critical': {
      accentClass: 'accent-destructive',
      icon: AlertCircle,
      variant: 'destructive' as const
    },
    'complete': {
      accentClass: 'accent-info',
      icon: CheckCircle,
      variant: 'default' as const
    }
  };

  const currentStatusConfig = statusConfig[summaryData.overallStatus];
  const StatusIcon = currentStatusConfig.icon;

  // Progress bar color using accent system
  const getProgressAccent = () => {
    if (summaryData.progressPercentage >= 100) return 'accent-success';
    if (summaryData.progressPercentage >= 75) return 'accent-info';
    if (summaryData.progressPercentage >= 50) return 'accent-warning';
    return 'accent-orange';
  };

  return (
    <Card className="hover:shadow-lg transition-all duration-200 accent-purple border-l-4 border-l-[var(--accent-border)] group cursor-default">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <div className="p-1.5 rounded-md accent-purple bg-[var(--accent-bg-outline)] group-hover:scale-110 transition-transform">
                <Zap className="h-4 w-4 text-[var(--accent-text-on-outline)]" />
              </div>
              <span>Quick Summary</span>
            </CardTitle>
            <p className="text-xs text-muted-foreground ml-9">
              This week at a glance
            </p>
          </div>
          <Badge 
            variant={currentStatusConfig.variant}
            className="flex items-center gap-1 transition-all group-hover:scale-105"
          >
            <StatusIcon className="h-3 w-3" />
            {summaryData.statusMessage}
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Progress Bar */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Weekly Progress</span>
            <span className="text-sm font-bold">
              {summaryData.hoursLogged.toFixed(1)} / {summaryData.expectedHours}h
            </span>
          </div>
          <div className={getProgressAccent()}>
            <Progress 
              value={summaryData.progressPercentage} 
              className="h-2"
              indicatorClassName="bg-[var(--accent-bg-solid)]"
            />
          </div>
        </div>

        {/* Key Metrics Grid */}
        <TooltipProvider>
          <div className="grid grid-cols-2 gap-3">
            {/* Billable Hours - Clickable */}
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={() => onNavigate?.('/reports/billable')}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onNavigate?.('/reports/billable');
                    }
                  }}
                  className="flex items-start gap-2 p-2.5 rounded-lg accent-info bg-[var(--accent-bg-outline)] border border-[var(--accent-border)] cursor-pointer hover:shadow-md hover:scale-[1.02] active:scale-[0.98] transition-all text-left w-full group/metric"
                  aria-label="View billable hours report"
                >
                  <TrendingUp className="h-4 w-4 text-[var(--accent-text-on-outline)] mt-0.5 flex-shrink-0 group-hover/metric:scale-110 transition-transform" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-[var(--accent-text-on-outline)] font-medium">
                      Billable
                    </p>
                    <p className="text-lg font-bold text-[var(--accent-text-on-outline)]">
                      {summaryData.billablePercentage.toFixed(0)}%
                    </p>
                    <p className="text-xs text-[var(--accent-text-on-outline)] opacity-80">
                      {summaryData.billableHours.toFixed(1)}h
                    </p>
                  </div>
                  <ExternalLink className="h-3 w-3 text-[var(--accent-text-on-outline)] opacity-0 group-hover/metric:opacity-100 transition-opacity" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="max-w-xs">
                <p className="font-semibold mb-1">Billable Hours</p>
                <p className="text-xs text-muted-foreground">
                  {summaryData.billableHours.toFixed(1)} of {summaryData.hoursLogged.toFixed(1)} hours logged this week are billable to clients.
                  {summaryData.billablePercentage >= 80 ? ' Great utilization!' : summaryData.billablePercentage >= 60 ? ' Good progress.' : ' Consider increasing billable work.'}
                </p>
                <p className="text-xs text-muted-foreground mt-2 font-medium">
                  💡 Click to view detailed billable hours report
                </p>
              </TooltipContent>
            </Tooltip>

            {/* Entries Count - Clickable */}
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={() => onNavigate?.('/timesheet')}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onNavigate?.('/timesheet');
                    }
                  }}
                  className="flex items-start gap-2 p-2.5 rounded-lg accent-purple bg-[var(--accent-bg-outline)] border border-[var(--accent-border)] cursor-pointer hover:shadow-md hover:scale-[1.02] active:scale-[0.98] transition-all text-left w-full group/metric"
                  aria-label="View timesheet entries"
                >
                  <Clock className="h-4 w-4 text-[var(--accent-text-on-outline)] mt-0.5 flex-shrink-0 group-hover/metric:scale-110 transition-transform" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-[var(--accent-text-on-outline)] font-medium">
                      Entries
                    </p>
                    <p className="text-lg font-bold text-[var(--accent-text-on-outline)]">
                      {summaryData.entriesCount}
                    </p>
                    <p className="text-xs text-[var(--accent-text-on-outline)] opacity-80">
                      {summaryData.daysWithEntries} days
                    </p>
                  </div>
                  <ExternalLink className="h-3 w-3 text-[var(--accent-text-on-outline)] opacity-0 group-hover/metric:opacity-100 transition-opacity" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="max-w-xs">
                <p className="font-semibold mb-1">Time Entries</p>
                <p className="text-xs text-muted-foreground">
                  You have {summaryData.entriesCount} time {summaryData.entriesCount === 1 ? 'entry' : 'entries'} across {summaryData.daysWithEntries} {summaryData.daysWithEntries === 1 ? 'day' : 'days'} this week.
                  {summaryData.daysWithEntries >= 5 ? ' Complete week!' : summaryData.daysWithEntries >= 3 ? ' Good coverage.' : ' Add more entries.'}
                </p>
                <p className="text-xs text-muted-foreground mt-2 font-medium">
                  💡 Click to view and edit your timesheet
                </p>
              </TooltipContent>
            </Tooltip>
          </div>
        </TooltipProvider>

        {/* Status Message */}
        {summaryData.status === 'draft' && summaryData.daysUntilDue >= 0 && (
          <div className={`flex items-start gap-2 p-3 rounded-lg border ${currentStatusConfig.accentClass} bg-[var(--accent-bg-outline)] border-[var(--accent-border)]`}>
            <StatusIcon className="h-4 w-4 mt-0.5 text-[var(--accent-text-on-outline)]" />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-[var(--accent-text-on-outline)]">
                {summaryData.daysUntilDue === 0 ? (
                  <>Due today at {summaryData.deadlineTime}</>
                ) : summaryData.daysUntilDue === 1 ? (
                  <>Due tomorrow</>
                ) : (
                  <>{summaryData.daysUntilDue} days until due</>
                )}
              </p>
              <p className="text-xs text-[var(--accent-text-on-outline)] opacity-80">
                {summaryData.progressPercentage >= 100 ? (
                  <>Ready to submit</>
                ) : (
                  <>{(summaryData.expectedHours - summaryData.hoursLogged).toFixed(1)}h remaining</>
                )}
              </p>
            </div>
          </div>
        )}

        {summaryData.status === 'draft' && summaryData.daysUntilDue < 0 && (
          <div className={`flex items-start gap-2 p-3 rounded-lg border ${currentStatusConfig.accentClass} bg-[var(--accent-bg-outline)] border-[var(--accent-border)]`}>
            <StatusIcon className="h-4 w-4 mt-0.5 text-[var(--accent-text-on-outline)]" />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-[var(--accent-text-on-outline)]">
                Overdue by {Math.abs(summaryData.daysUntilDue)} day{Math.abs(summaryData.daysUntilDue) > 1 ? 's' : ''}
              </p>
              <p className="text-xs text-[var(--accent-text-on-outline)] opacity-80">
                Submit as soon as possible
              </p>
            </div>
          </div>
        )}

        {/* Primary Action Button */}
        {onNavigate && summaryData.status === 'draft' && (
          <Button
            onClick={() => onNavigate('/timesheet')}
            className="w-full group/btn"
            variant={summaryData.overallStatus === 'critical' ? 'destructive' : 'default'}
          >
            <span>
              {summaryData.progressPercentage >= 100 ? 'Review & Submit' : 'Continue Timesheet'}
            </span>
            <ArrowRight className="ml-2 h-4 w-4 group-hover/btn:translate-x-1 transition-transform" />
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
